import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useCollapse } from 'react-collapsed';
import ReactMarkdown from 'react-markdown';
import { store } from 'components/Store';
import getAppSite from 'utils/getAppSite';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { copyTextToClipboard } from 'components/Dashboard/helpers/copyFunctionality';
import CollapsibleSection, {
  onEnterOrSpace,
} from 'components/Dashboard/StakeHolders/StakeholderPage/ui/CollapsibleSection';

const addBiographyToReport = ({ dispatch, navigate, StakeholderInformation }) => {
  dispatch({
    type: 'MODIFY_SECTION',
    parameter: 'stakeholderToCreateReport',
    value: { type: 'StakeholderBioComponent', stakeholders: [StakeholderInformation] },
  });
  navigate({
    pathname: '/reports/create',
  });
};

const Biography = (props) => {
  const { StakeholderInformation, profile } = props;
  const globalState = useContext(store);
  const { dispatch } = globalState;
  const navigate = useNavigate();
  const isUsSite = getAppSite() === 'usa';

  return (
    <CollapsibleSection
      title={isUsSite ? 'Fact sheet' : 'Biography'}
      titleClassName='member-info-subtitle stakeholder-bio-subtitle'
      actions={(isExpanded) =>
        isUsSite ? (
          <div className='flex-centered'>
            {isExpanded && (
              <span
                className='light-40-text paragraph-p3 mr-2'
                data-tooltip-content='This fact sheet is AI-generated using information from Wikipedia and may not always be accurate or up to date.'
                data-tooltip-id='general-tooltip'
                data-tooltip-class-name='general-tooltip pricing-feature-tooltip tooltip-width-300'
                onClick={(e) => e.stopPropagation()}
              >
                AI disclaimer
              </span>
            )}
            <FactSheetActionsMenu StakeholderInformation={StakeholderInformation} profile={profile} />
          </div>
        ) : (
          <button
            className='general-button circle-button hovered-button'
            data-tooltip-id='general-tooltip'
            data-tooltip-content='Add stakeholder biography to a report'
            onClick={(e) => {
              e.stopPropagation();
              addBiographyToReport({ dispatch, navigate, StakeholderInformation });
            }}
          >
            <span className='icon-document' />
          </button>
        )
      }
    >
      <BiographyContent {...props} />
    </CollapsibleSection>
  );
};

const markdownToPlainText = (markdown) =>
  (markdown ?? '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '• ')
    .replace(/^\s*\d+\.\s+/gm, '')
    .trim();

const FactSheetActionsMenu = ({ StakeholderInformation, profile }) => {
  const globalState = useContext(store);
  const { dispatch } = globalState;
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  useOutsideElement(menuRef, () => setIsOpen(false));

  return (
    <div className='position-relative' ref={menuRef}>
      <button
        className='general-button circle-button hovered-button'
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prevOpen) => !prevOpen);
        }}
        style={{ marginBottom: '-2px' }}
      >
        <span className='icon-three-dots text-main-blue-50' />
      </button>
      {isOpen && (
        <div
          className='bg-main-white border box-shadow rounded position-absolute z-extra-max py-2'
          style={{ right: 0, top: '38px', minWidth: '240px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className='w-100 list-action-button general-button text-left flex-centered px-3 py-2'
            onClick={() => {
              copyTextToClipboard(markdownToPlainText(profile), 'Fact sheet copied');
              setIsOpen(false);
            }}
          >
            <span className='icon-copy-thin icon-general-container' />
            <span>Copy text</span>
          </button>
          <button
            className='w-100 list-action-button general-button text-left flex-centered px-3 py-2'
            onClick={() => {
              addBiographyToReport({ dispatch, navigate, StakeholderInformation });
              setIsOpen(false);
            }}
          >
            <span className='icon-document icon-general-container' />
            <span>Add fact sheet to a report</span>
          </button>
        </div>
      )}
    </div>
  );
};

const BiographyHeading = ({ children }) => <h5 className='text-main-blue mb-3 mt-3'>{children}</h5>;

const COLLAPSED_BULLET_POINTS = 3;
const bulletLinePattern = /^\s*(?:[-*+]|\d+\.)\s+/;
const ELLIPSIS = '…';

// Truncating by bullet count (not CSS line-clamp) so a bullet that wraps onto a second row still counts as one item.
const truncateProfileByBulletPoints = (markdown, maxBulletPoints) => {
  if (!markdown) return { content: markdown, isTruncated: false };
  const lines = markdown.split('\n');
  const collectedLines = [];
  let bulletCount = 0;
  let isTruncated = false;
  for (const line of lines) {
    if (bulletCount >= maxBulletPoints) {
      if (line.trim()) isTruncated = true;
      continue;
    }
    collectedLines.push(line);
    if (bulletLinePattern.test(line)) bulletCount += 1;
  }
  if (isTruncated && collectedLines.length) {
    const lastIndex = collectedLines.length - 1;
    collectedLines[lastIndex] = collectedLines[lastIndex].replace(/[.\s]*$/, '') + ELLIPSIS;
  }
  return { content: collectedLines.join('\n'), isTruncated };
};

const BiographyContent = (props) => {
  const { profile, isAuthenticated, setShowContactsPopup } = props;
  const [activeEllipsis, setActiveEllipsis] = useState(false);
  const location = useLocation();
  const createMarkup = (body) => {
    return { __html: body };
  };
  const [isExpanded, setExpanded] = useState(false);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });

  const isEllipsisActive = (e) => {
    if (e) {
      return e.offsetHeight < e.scrollHeight;
    }
  };

  const bioElement = useCallback((node) => {
    if (node !== null) {
      setActiveEllipsis(isEllipsisActive(node));
    }
  }, []);

  const isUsSite = getAppSite() === 'usa';

  const { content: truncatedProfile, isTruncated } = useMemo(
    () =>
      isUsSite
        ? truncateProfileByBulletPoints(profile, COLLAPSED_BULLET_POINTS)
        : { content: profile, isTruncated: false },
    [profile, isUsSite]
  );

  const showSeeMore = isExpanded || (isUsSite ? isTruncated : activeEllipsis);

  const toggleFullBiography = () => {
    if (isAuthenticated) {
      setExpanded((prevExpanded) => !prevExpanded);
    } else {
      setShowContactsPopup({
        show: true,
        url: `/register?reg_source=stakeholder-see-more&returnUrl=${location.pathname}`,
      });
    }
  };

  return (
    <>
      <div className={`${isUsSite ? '' : 'pt-1'}`}>
        <div
          className={`biography-element ${isUsSite ? '' : 'hidden-lines biography-element-hidden'} ${isExpanded ? 'd-none' : ''}`}
          ref={isUsSite ? undefined : bioElement}
          dangerouslySetInnerHTML={!isUsSite ? createMarkup(profile) : undefined}
        >
          {isUsSite ? (
            <ReactMarkdown components={{ h1: BiographyHeading, h2: BiographyHeading, h3: BiographyHeading }}>
              {truncatedProfile}
            </ReactMarkdown>
          ) : null}
        </div>
      </div>

      <div {...getCollapseProps()}>
        <div
          className={`biography-element ${isExpanded ? 'd-block' : 'd-none'}`}
          dangerouslySetInnerHTML={!isUsSite ? createMarkup(profile) : undefined}
        >
          {isUsSite ? (
            <ReactMarkdown components={{ h1: BiographyHeading, h2: BiographyHeading, h3: BiographyHeading }}>
              {profile}
            </ReactMarkdown>
          ) : null}
        </div>
      </div>
      <p
        className='mb-2 text-light-blue paragraph-p2 pointer toggle-no-button'
        style={{ visibility: showSeeMore ? 'visible' : 'hidden' }}
        {...getToggleProps({ onClick: toggleFullBiography, onKeyDown: onEnterOrSpace(toggleFullBiography) })}
      >
        See {isExpanded ? 'less' : 'more'}
      </p>
    </>
  );
};

export { truncateProfileByBulletPoints };
export default Biography;
